import {
  createQueryService,
  createMutationService,
  getQueryStringWithoutUndefined,
  createBlobService,
  createFormDataService,
} from "utils/factories";
import { IHistoryData } from "../domains/pslHistory.domain";
import { PaginatedQueryResult } from "service/generic.types";
import { IUserFullScope } from "modules/User/service/user.service";

export class NetworkCurrencyDTO {
  id: string;
  name: string;
  code: string;
  isDecimalAllowed: boolean;
}

export class CurrencyData {
  constructor(
    public id: string,
    public Description: string,
    public CurrencyKey: string,
    public isDecimalAllowed: boolean
  ) {}

  static FromBackend(data: NetworkCurrencyDTO): CurrencyData {
    return new CurrencyData(
      data.id,
      data.name,
      data.code,
      data.isDecimalAllowed
    );
  }

  static toSelect(data: CurrencyData) {
    return {
      value: data.CurrencyKey,
      label: data.Description,
    };
  }

  static toSimpleSelect() {
    return [
      { value: "USD", label: "USD" },
      { value: "EUR", label: "EUR" },
      { value: "CHF", label: "CHF" },
    ];
  }
}

export interface IdNamePair {
  id: number;
  name: string;
}

export default class PromoItemsService {
  static readonly approvePsl = createMutationService<
    any,
    { pslId: string | number }
  >(
    (params) =>
      `/api/HackathonPsl/ApprovePsl${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined(params)}`,
    "POST"
  );

  static readonly rejectPsls = createMutationService<
    any,
    { pslIds: (string | number)[]; comment: string }
  >(
    (params) =>
      `/api/HackathonPsl/RejectPsl${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined(params)}`,
    "POST"
  );

  static readonly approveQuality = createMutationService<
    any,
    { pslId: string | number }
  >(
    (params) =>
      `/api/HackathonPsl/ApproveQuality${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined(params)}`,
    "POST"
  );

  static readonly rejectQuality = createMutationService<
    any,
    { pslIds: (string | number)[]; comment: string }
  >(
    (params) =>
      `/api/HackathonPsl/RejectQuality${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined(params)}`,
    "POST"
  );

  static readonly deletePsl = createMutationService<
    any,
    { pslId: string | number }
  >(({ pslId }) => `/api/HackathonPsl/DeletePsl?pslId=${pslId}`, "POST");

  static readonly toggleFavoriteMaterial = createMutationService<
    never,
    { pslId: string | number }
  >(
    ({ pslId }) => `/api/HackathonPsl/AddOrRemovePslFavorites?pslId=${pslId}`,
    "POST"
  );

  static readonly massImportMaterials = createFormDataService<
    any,
    { file: File }
  >("/api/Psl/PostPslListFromExcel", "POST");
  //test

  static readonly importCampaigns = createFormDataService<
    any,
    { file: File }
  >("/api/order-windows/import", "POST");

  static readonly deletePromoMaterialMedia = createMutationService<
    any,
    { fileId: string }
  >(({ fileId }) => `/api/promo-items/files/${fileId}`, "DELETE");

  static readonly deletePromoHeaderMedia = createMutationService<
    any,
    { fileId: string }
  >(({ fileId }) => `/api/promo-header/files/${fileId}`, "DELETE");

  static readonly uploadPromoMediaFromLink = createMutationService<
    any,
    { link: string; materialId?: string }
  >(
    ({ link, materialId }) =>
      `/api/promo-items/files/upload-file-from-link?link=${link}${
        materialId ? `&materialId=${materialId}` : ""
      }`,
    "POST"
  );
  static readonly uploadPromoMaterialsMedia = createFormDataService<any, any>(
    (params) =>
      `/api/promo-items/files/upload-file${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined({
        document: params.document,
        // materialId: params.materialId,
        isQuality: params.isQuality,
      })}`,
    "POST"
  );

  static readonly uploadPromoHeaderMedia = createFormDataService<any, any>(
    (params) =>
      `/api/promo-header/files/upload-file${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined({
        promoId: params.promoId,
      })}`,
    "POST"
  );

  static readonly exportMaterials = createBlobService<any, any>(
    (params) =>
      `/api/HackathonPsl/GetPslMaterialsAsExcelV2${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined({
        searchTerm: params.searchTerm,
        take: params.take,
        skip: params.skip,
      })}`,
    "POST"
  );
  static readonly getMaterials = createMutationService<
    PaginatedQueryResult<any>,
    any
  >(
    (params) =>
      `/api/HackathonPsl/GetPslMaterialsV2${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined({
        searchTerm: params.searchTerm,
        take: params.take,
        skip: params.skip,
      })}`,
    "POST"
  );
  static readonly getMaterialsTemplate = createBlobService<any, any>(
    "/api/HackathonPsl/GetMaterialListExcelTemplate",
    "GET"
  );
  static readonly getPSLTemplate = createBlobService<any, any>(
    "/api/Psl/GetPslListExcelTemplate",
    "GET"
  );
  static readonly getCampaignTemplate = createBlobService<any, any>(
    "/api/order-windows/download-template",
    "GET"
  );
  static readonly getPromoEntries = createMutationService<
    PaginatedQueryResult<any>,
    any
  >(
    (params) =>
      `/api/HackathonPsl/GetPslEntriesV2${
        !params ? "" : "?"
      }${getQueryStringWithoutUndefined({
        searchTerm: params.searchTerm,
        take: params.take,
        skip: params.skip,
      })}`,
    "POST"
  );

  static readonly addPslVendorsWithMaterials = createMutationService<any, any>(
    "/api/HackathonPsl/AddPSLVendorsWithMaterials",
    "POST"
  );
  static readonly localizePslVendorsWithMaterials = createMutationService<
    any,
    any
  >("/api/HackathonPsl/LocalizePslWithMaterials", "POST");

  static readonly localizeBulkPsl = createMutationService<any[], number[]>(
  "/api/HackathonPsl/LocalizeBulkPsl",
  "POST"
);

  static readonly updatePslVendorsWithMaterials = createMutationService<
    any,
    any
  >("/api/HackathonPsl/UpdatePSLVendorsWithMaterials", "POST");

  static readonly updateMaterialsForPslEntryBulk = createMutationService<
    any,
    any[]
  >("/api/HackathonPsl/UpdateMaterialsForPslEntryBulk", "POST");

  static readonly updateChildPslWithMaterials = createMutationService<any, any>(
    "/api/HackathonPsl/UpdateChildPslWithMaterials",
    "POST"
  );

  static readonly reactivatePslEntry = createMutationService<any, any>(
    "/api/HackathonPsl/ReactivatePslEntryV2",
    "POST"
  );

  static readonly getPslAmdrMaterials = createQueryService<
    any[],
    {
      locationCompanyCodes?: string;
      searchTerm?: string;
      materialgroup?: string;
      locationMarkets?: string;
      locationZones?: string;
      plants?: string;
    }
  >("/api/HackathonPsl/GetPslAmdrMaterials");

  static readonly getPslAmdrSuppliers = createQueryService<
    { id: string | number; name: string }[],
    {
      searchTerm?: string;
      locationCompanyCodes: string;
      isCreatePSL?: boolean;
    }
  >("/api/HackathonPsl/GetPslAmdrSuppliers");

  static readonly getPslAmdrSuppliersHierarchy = createQueryService<
    { id: string | number; name: string }[],
    {
      searchTerm?: string;
    }
  >("/api/HackathonPsl/GetPslAmdrSuppliersHierarchy");

  static readonly fetchUserEmails = createQueryService<
    string[],
    { searchTerm?: string }
  >("/api/CompetitiveBidding/ApproverEmails");

  static fetchPSLHistory = createQueryService<
    IHistoryData,
    { pslId: string | number }
  >(`/api/HackathonPsl/GetPslHistory`);

  static fetchMaterialHistory = createQueryService<
    IHistoryData,
    { materialId: string | number }
  >(`/api/HackathonPsl/GetPslHistory`);

  static readonly fetchPslEntry = createQueryService<any, { id: string }>(
    `/api/HackathonPsl/GetPslEntry`
  );
  static readonly fetchMaterialsForPslEntry = createQueryService<
    PaginatedQueryResult<any>,
    { id: string; includeDeleted?: boolean; skip?: number; take?: number }
  >(`/api/HackathonPsl/GetMaterialsForPslEntry`);

  static async fetchPslInitialData({
    pslId,
  }): Promise<{ vendor: any; materials?: PaginatedQueryResult<any> }> {
    const vendor = await PromoItemsService.fetchPslEntry({ id: pslId });
    return { vendor };
  }
  // Following is addapted from Contract Browser code. Here's the original for refference O.o:
  // static async fetchChildrenSuppliersV2(locationCompanyCodes: string = "", parentId: string, pslId?: number, zones: string = ""): Promise<ChildCodeResults> {
  //   const response = await AxiosWrapper.post('/api/HackathonPsl/GetPslAmdrSuppliersChildren',
  //     { locationCompanyCodes, childrenOf: parentId, parentId: pslId, zones }
  //   );
  //   return response.data;
  // }
  static readonly fetchChildrenSuppliersV2 = createMutationService<
    { results: any },
    {
      locationCompanyCodes: string;
      childrenOf: string;
      parentId?: number;
      zones: string;
    }
  >(`/api/HackathonPsl/GetPslAmdrSuppliersChildren`);

  static readonly fetchPslAmdrPlantsV2 = createQueryService<
    {
      code: string;
      name: string;
    }[],
    {
      text: string;
      locationZones: string;
      locationMarkets: string;
      locationCompanyCodes: string;
      materialNumber: string;
    }
  >(`/api/HackathonPsl/GetPslAmdrPlantsV2`);

  static readonly getAmdrSuppliersHierarchyWithScope = createMutationService<
    IUserFullScope[],
    number[] | string[]
  >(`/api/HackathonPsl/GetAmdrSuppliersHierarchyWithScope?userScope=false`);
}
